import React, { useCallback, useEffect, useMemo, useState } from "react"
import { X, Save, RotateCcw, Trash2 } from "lucide-react"
import JsonEditor from "./json-editor"

import { AI_MODELS, getToolById } from "../registry"
import { loadToolConfig, parseJsonSafe, saveToolConfig } from "./storage"
import { summariseSchema, modelSupportsTemperature } from "./schema-utils"
import Button from "../../../components/ui/button/button"
import Input from "../../../components/ui/inputs/input"
import Textarea from "../../../components/ui/inputs/textarea"
import Select from "../../../components/ui/select/select"
import Checkbox from "../../../components/ui/checkbox/checkbox"

/**
 * Editable detail drawer for a selected flow node (Stage 2). Slides in from the
 * right while the canvas stays visible.
 *
 * Per node type:
 *   - agent / tool        -> edit the live `ai_tooling/{toolId}` config; its own
 *                            Save writes to Firestore (+ Reset to defaults).
 *   - agent / placeholder -> edit the draft stored on the node; "Apply to flow"
 *                            pushes it into the graph (persisted by canvas Save).
 *   - trigger/router/step -> edit label + description; "Apply to flow" as above.
 *
 * The JSON schema is edited in CodeMirror 6 with live validation; "Expected
 * outputs" re-derives from the parsed schema so it can never drift.
 */

function NodeDrawer({ node, onClose, onUpdateNode, onDeleteNode }) {
	const isAgent = node?.type === "agent"
	const isTool = isAgent && node?.data?.binding === "tool"

	// Agent config fields.
	const [model, setModel] = useState("")
	const [temperature, setTemperature] = useState("0")
	const [systemInstructions, setSystemInstructions] = useState("")
	const [userInstructions, setUserInstructions] = useState("")
	const [schemaText, setSchemaText] = useState("")
	const [schemaName, setSchemaName] = useState("")
	const [schemaStrict, setSchemaStrict] = useState(false)

	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [savedNote, setSavedNote] = useState(null)

	const applyConfig = useCallback((cfg) => {
		setModel(cfg.model || "")
		setTemperature(cfg.temperature != null ? String(cfg.temperature) : "0")
		setSystemInstructions(cfg.system_instructions || "")
		setUserInstructions(cfg.user_instructions || "")
		setSchemaText(cfg.json_schema ? JSON.stringify(cfg.json_schema, null, 4) : "")
		setSchemaName(cfg.json_schema_name || "")
		setSchemaStrict(!!cfg.json_schema_strict)
	}, [])

	useEffect(() => {
		let cancelled = false
		setError(null)
		setSavedNote(null)

		if (isTool) {
			const tool = getToolById(node.data.toolId)
			const defaults = tool?.defaults || {}
			setLoading(true)
			;(async () => {
				try {
					const saved = await loadToolConfig(node.data.toolId)
					if (cancelled) return
					applyConfig({ ...defaults, ...(saved || {}) })
				} catch (err) {
					if (!cancelled) {
						setError(err?.message || String(err))
						applyConfig(defaults)
					}
				} finally {
					if (!cancelled) setLoading(false)
				}
			})()
		} else if (isAgent) {
			applyConfig(node.data.draft || {})
		}

		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [node?.id])

	const modelOptions = useMemo(() => Object.fromEntries(AI_MODELS.map((m) => [m, m])), [])
	const tempSupported = useMemo(() => modelSupportsTemperature(model), [model])
	const schemaParse = useMemo(() => parseJsonSafe(schemaText), [schemaText])
	const outputs = useMemo(
		() => (schemaParse.ok ? summariseSchema(schemaParse.value) : []),
		[schemaParse]
	)

	const collectConfig = () => ({
		model,
		temperature: Number(temperature),
		system_instructions: systemInstructions,
		user_instructions: userInstructions,
		json_schema: schemaParse.ok ? schemaParse.value : schemaText.trim() === "" ? {} : null,
		json_schema_name: schemaName.trim(),
		json_schema_strict: !!schemaStrict,
	})

	const validateConfig = () => {
		if (!schemaParse.ok) {
			setError(`JSON schema is not valid JSON - ${schemaParse.error}`)
			return false
		}
		if (!schemaName.trim()) {
			setError("JSON schema name is required")
			return false
		}
		if (Number.isNaN(Number(temperature))) {
			setError("Temperature must be a number")
			return false
		}
		return true
	}

	const saveToolAgent = async () => {
		setError(null)
		if (!validateConfig()) return
		setSaving(true)
		try {
			await saveToolConfig(node.data.toolId, collectConfig())
			setSavedNote("Saved to AI Tooling")
		} catch (err) {
			setError(err?.message || String(err))
		} finally {
			setSaving(false)
		}
	}

	// Placeholder/prototype agents don't need a schema name - only reject
	// genuinely broken JSON. (Tool agents still go through the strict
	// validateConfig on Save config.)
	const applyAgentDraft = () => {
		setError(null)
		if (schemaText.trim() && !schemaParse.ok) {
			setError(`JSON schema is not valid JSON - ${schemaParse.error}`)
			return
		}
		if (Number.isNaN(Number(temperature))) {
			setError("Temperature must be a number")
			return
		}
		onUpdateNode?.(node.id, { draft: collectConfig() })
		setSavedNote("Applied to flow - use Save layout to persist")
	}

	const resetToDefaults = () => {
		const tool = getToolById(node?.data?.toolId)
		if (tool?.defaults) {
			applyConfig(tool.defaults)
			setSavedNote(null)
		}
	}

	if (!node) return null

	const renderConfigFields = () => (
		<>
			<div className="ai-flow-drawer-grid">
				<div className="ai-flow-drawer-field">
					<Select
						showLabel
						placeholder="Model:"
						value={model}
						selected={model}
						onSelect={(option) => setModel(option.option)}
						options={modelOptions}
					/>
				</div>
				<div className="ai-flow-drawer-field">
					{tempSupported ? (
						<Input
							type="text"
							placeholder="Temperature (0-2):"
							value={temperature}
							onChange={setTemperature}
							showLabel
						/>
					) : (
						<div className="ai-flow-drawer-temp-na">
							<span className="ai-flow-drawer-field-label">Temperature</span>
							<p>Not supported by {model}</p>
						</div>
					)}
				</div>
			</div>

			<div className="ai-flow-drawer-section">
				<span className="ai-flow-drawer-section-label">System instructions</span>
				<Textarea
					placeholder="System instructions"
					value={systemInstructions}
					onChange={setSystemInstructions}
					showLabel
					rows={10}
				/>
			</div>

			<div className="ai-flow-drawer-section">
				<span className="ai-flow-drawer-section-label">User instructions</span>
				<Textarea
					placeholder="User instructions"
					value={userInstructions}
					onChange={setUserInstructions}
					showLabel
					rows={8}
				/>
			</div>

			<div className="ai-flow-drawer-section">
				<Input
					type="text"
					placeholder="Schema name (snake_case):"
					value={schemaName}
					onChange={setSchemaName}
					showLabel
				/>
				<div className="ai-flow-drawer-strict">
					<Checkbox
						label="Strict mode"
						checked={!!schemaStrict}
						onClick={() => setSchemaStrict((prev) => !prev)}
					/>
				</div>
			</div>

			<div className="ai-flow-drawer-section">
				<span className="ai-flow-drawer-section-label">JSON response schema</span>
				<div className="ai-flow-drawer-cm">
					<JsonEditor value={schemaText} onChange={setSchemaText} />
				</div>
				{!schemaParse.ok && (
					<small className="ai-flow-drawer-schema-error">Invalid JSON - {schemaParse.error}</small>
				)}
			</div>

			<div className="ai-flow-drawer-section">
				<span className="ai-flow-drawer-section-label">
					Expected outputs
					<small> derived from schema</small>
				</span>
				{outputs.length === 0 ? (
					<p className="ai-flow-drawer-hint">No schema properties to derive from yet.</p>
				) : (
					<ul className="ai-flow-drawer-outputs">
						{outputs.map((o) => (
							<li key={o.key}>
								<code>{o.key}</code>
								<span className="ai-flow-drawer-output-type">{o.type}</span>
								{o.required && <span className="ai-flow-drawer-output-req">required</span>}
							</li>
						))}
					</ul>
				)}
			</div>
		</>
	)

	return (
		<aside className="ai-flow-drawer" role="dialog" aria-label={`${node.data?.label} details`}>
			<header className="ai-flow-drawer-header">
				<div className="ai-flow-drawer-header-text">
					<h3>{node.data?.label || "Node"}</h3>
				</div>
				<button type="button" className="ai-flow-drawer-close" onClick={onClose} aria-label="Close">
					<X size={18} />
				</button>
			</header>

			<div className="ai-flow-drawer-body">
				{error && (
					<div className="ai-panel-error" role="alert">
						{error}
					</div>
				)}
				{savedNote && <p className="ai-flow-drawer-saved-note">{savedNote}</p>}

				{/* Label + description live-update the node so the canvas reflects
				    edits immediately (persisted by the canvas Save layout). */}
				<div className="ai-flow-drawer-section">
					<span className="ai-flow-drawer-section-label">Label</span>
					<Input
						type="text"
						placeholder="Label"
						value={node.data?.label || ""}
						onChange={(value) => onUpdateNode?.(node.id, { label: value })}
						showLabel
					/>
				</div>
				<div className="ai-flow-drawer-section">
					<span className="ai-flow-drawer-section-label">Description</span>
					<Textarea
						placeholder="Description"
						value={node.data?.description || ""}
						onChange={(value) => onUpdateNode?.(node.id, { description: value })}
						showLabel
						rows={3}
					/>
				</div>

				{!isAgent && (
					<p className="ai-flow-drawer-hint">
						{node.type === "trigger" && "Pipeline entry point - no AI configuration."}
						{node.type === "router" && "Routing decision - the outgoing connections carry the conditions."}
						{node.type === "step" && "Deterministic code step - no model or prompt."}
					</p>
				)}

				{isAgent && loading && <p className="ai-flow-drawer-hint">Loading config...</p>}

				{isAgent && !loading && renderConfigFields()}
			</div>

			<footer className="ai-flow-drawer-footer">
				<span className="ai-flow-drawer-delete">
					<Button
						type="OUTLINE"
						small={true}
						label="Delete node"
						icon={<Trash2 size={13} />}
						onClick={() => onDeleteNode?.(node.id)}
						disabled={saving}
					/>
				</span>
				<div className="ai-flow-drawer-footer-actions">
					{isTool ? (
						<>
							<Button
								type="OUTLINE"
								label="Reset to defaults"
								icon={<RotateCcw size={13} />}
								onClick={resetToDefaults}
								disabled={saving}
							/>
							<Button
								label="Save config"
								icon={<Save size={14} />}
								loading={saving}
								loadingText="Saving..."
								onClick={saveToolAgent}
								disabled={saving || !schemaParse.ok}
							/>
						</>
					) : isAgent ? (
						<Button
							label="Apply config"
							icon={<Save size={14} />}
							onClick={applyAgentDraft}
							disabled={schemaText.trim() !== "" && !schemaParse.ok}
						/>
					) : null}
				</div>
			</footer>
		</aside>
	)
}

export default NodeDrawer
